import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["autoPlacement", "buttonRef", "cornerToRound", "disabled", "icon", "renderIcon", "isToolbarActive", "onClick", "onMouseDown", "placement", "rotate", "title", "tooltip", "use", "isTrellisTheme", "variant", "width", "aria-label"];
import { useMapTokenPropsToCSS } from 'foundations-components/hooks/useMapTokenPropsToCSS';
import { useTrellisFocusRing } from 'foundations-components/hooks/useTrellisFocusRing';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { filterProps } from 'foundations-components/utils/filterProps';
import styled, { useTheme } from 'foundations-theming/utils';
import { forwardRef, useCallback } from 'react';
import { mergeProps } from 'react-aria';
import { getToolbarVariantStyles, toolbarIconButtonSizeVariants } from './constants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TitleWrapper = styled.span.withConfig({
  displayName: "ToolbarIconButton__TitleWrapper"
})([".hs-private.hs-private.hs-private &{", ";line-height:", ";margin-left:", ";}"], ({
  theme
}) => theme.typography['heading-25'], ({
  $lineHeight
}) => $lineHeight, ({
  theme
}) => theme.space['200']);
const computeBorderRadius = (theme, cornerToRound) => {
  switch (cornerToRound) {
    case 'left top':
    case 'top left':
      return {
        borderTopLeftRadius: theme === null || theme === void 0 ? void 0 : theme.borderRadius['100']
      };
    case 'left bottom':
    case 'bottom left':
      return {
        borderBottomLeftRadius: theme === null || theme === void 0 ? void 0 : theme.borderRadius['100']
      };
    case 'right top':
    case 'top right':
      return {
        borderTopRightRadius: theme === null || theme === void 0 ? void 0 : theme.borderRadius['100']
      };
    case 'bottom right':
    case 'right bottom':
      return {
        borderBottomRightRadius: theme === null || theme === void 0 ? void 0 : theme.borderRadius['100']
      };
    default:
      return {};
  }
};
const IconButton = styled.button.withConfig({
  displayName: "ToolbarIconButton__IconButton"
})([".hs-private.hs-private.hs-private &{align-items:center;display:flex;justify-content:center;transition:all 150ms ease-out;padding:", ";box-shadow:none;", " ", " ", " ", " ", ";}.hs-private.hs-private.hs-private &[aria-disabled='true']{cursor:not-allowed;}.hs-private.hs-private.hs-private & svg[data-icon-name]{transform:rotate(", ");}"], ({
  theme
}) => theme.space['100'], ({
  $colorStyles
}) => $colorStyles, ({
  $borderStyles
}) => $borderStyles, ({
  $sizeStyles
}) => $sizeStyles, ({
  $focusRingStyles
}) => $focusRingStyles, ({
  $cssStyles
}) => $cssStyles, ({
  $rotate
}) => $rotate);
const ToolbarIconButton = (_ref, ref) => {
  var _variantStyles$border;
  let {
      autoPlacement,
      buttonRef,
      cornerToRound,
      disabled,
      icon: Icon,
      renderIcon,
      isToolbarActive,
      onClick,
      onMouseDown,
      placement,
      rotate = '0deg',
      title,
      tooltip,
      use,
      isTrellisTheme = false,
      variant = 'default',
      width,
      ['aria-label']: ariaLabel
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const theme = useTheme();
  const {
    tokenProps,
    domProps
  } = filterProps(props);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const handleClick = useCallback(e => {
    if (disabled || !onClick) return;
    onClick(e);
  }, [disabled, onClick]);
  const handleMouseDown = useCallback(() => {
    if (disabled || !onMouseDown) return;
    onMouseDown();
  }, [disabled, onMouseDown]);
  const {
    focusProps,
    focusRingStyles
  } = useTrellisFocusRing();
  // Chain these event handlers rather than overriding
  const mergedProps = mergeProps({
    onClick: handleClick,
    onMouseDown: handleMouseDown
  }, focusProps, domProps);
  const variantStyles = typeof variant === 'string' ? getToolbarVariantStyles(isTrellisTheme)[variant] : variant;
  const colorStyles = {
    backgroundColor: isToolbarActive ? variantStyles.backgroundColorActive : variantStyles.backgroundColorInactive,
    color: isToolbarActive ? variantStyles.fontColorActive : variantStyles.fontColorInactive,
    '&:hover': {
      backgroundColor: isToolbarActive ? variantStyles.backgroundColorActiveHover : variantStyles.backgroundColorInactiveHover
    },
    '&[aria-disabled="true"] svg[data-icon-name]': {
      color: variantStyles.fontColorInactive
    }
  };
  const borderStyles = Object.assign({
    borderWidth: theme.borderWidth['0'],
    borderBlockEndWidth: theme.borderWidth['100'],
    borderBlockStartWidth: theme.borderWidth['100'],
    borderColor: isToolbarActive ? variantStyles.borderColorActive : variantStyles.borderColorInactive,
    '&:hover': {
      borderColor: isToolbarActive ? (_variantStyles$border = variantStyles.borderColorActiveHover) !== null && _variantStyles$border !== void 0 ? _variantStyles$border : variantStyles.borderColorActive : variantStyles.borderColorInactive
    },
    borderStyle: 'solid',
    borderRadius: theme.borderRadius['0']
  }, computeBorderRadius(theme, cornerToRound));
  const sizeStyles = Object.assign({}, toolbarIconButtonSizeVariants[use]);
  if (width) sizeStyles.width = width;
  return /*#__PURE__*/_jsx(Tooltip, {
    title: tooltip,
    autoPlacement: autoPlacement,
    placement: placement,
    children: /*#__PURE__*/_jsxs(IconButton, Object.assign({}, mergedProps, {
      "aria-disabled": disabled,
      "aria-label": ariaLabel,
      $colorStyles: colorStyles,
      $cssStyles: cssStyles,
      $borderStyles: borderStyles,
      $focusRingStyles: focusRingStyles,
      $isToolbarActive: isToolbarActive,
      $rotate: rotate,
      $sizeStyles: sizeStyles,
      ref: ref || buttonRef,
      children: [Icon ? /*#__PURE__*/_jsx(Icon, {
        size: "inherit"
      }) : renderIcon && renderIcon(), title &&
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      _jsx(TitleWrapper, {
        $lineHeight: sizeStyles.lineHeight,
        children: title
      })]
    }))
  });
};
export default /*#__PURE__*/forwardRef(ToolbarIconButton);